import { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import Sidebar from "../components/Sidebar";
import { PPTViewer } from "../components/PPTViewer";
import { FullScreenPPTViewer } from "../components/FullScreenPPTViewer";
import { VideoSection } from "../components/VideoSection";
import AssessmentSection from "../components/AssessmentSection";
import { PDFViewer } from "../components/PDFViewer";
import { CompletionBadge } from "../components/CompletionBadge";
import ModuleFeedback from "../components/ModuleFeedback";
import { Warm } from "./Warm";

import AppShell from "../components/ui/AppShell";
import { Button, Card, EmptyState } from "../components/ui";
import { SwapIn } from "../components/ui/motion";
import { Award, Check, Document, Module, Quiz, Slides, Video } from "../components/icons";
import ReviewSection from "../components/ReviewSection";

import { API_URL, getHeaders } from "../config";
import { openAssignedContent } from "../services/AssignmentService";
import { useScreenTime } from "../context/ScreenTimeContext";
import { useContentProgress } from "../hooks/useContentProgress";
import "../assets/styles/button.css";

/** Locate the lesson with this id: { catIdx, itemIdx } or null. */
const findLesson = (categories, lessonId) => {
  const id = String(lessonId);
  for (let catIdx = 0; catIdx < categories.length; catIdx += 1) {
    const itemIdx = (categories[catIdx].items || []).findIndex(
      (it) => String(it._id) === id
    );
    if (itemIdx >= 0) return { catIdx, itemIdx };
  }
  return null;
};

/**
 * Resolve a one-shot "openTarget" (set by My Learning when opening an assigned
 * item) to a position within the loaded categories.
 *
 * Returns null when there was no target at all — the ordinary case, where the
 * caller is free to pick its own defaults. When a target WAS present but its
 * content isn't in this section, it returns `{ unresolved: true }` instead of
 * null: the learner explicitly asked for something, and quietly opening the
 * section's first lesson instead is the bug this guard exists to prevent.
 *
 * `assessmentId`/`videoId` name the exact item to open inside the lesson, since
 * a lesson can hold more than one of either.
 */
const resolveOpenTarget = (categories) => {
  let target;
  try {
    target = JSON.parse(localStorage.getItem("openTarget"));
  } catch {
    target = null;
  }
  if (!target?.contentId) return null;
  localStorage.removeItem("openTarget");

  const id = String(target.contentId);

  // Not in the section we loaded — the caller fetches it by assignment instead.
  const missing = { unresolved: true, target };

  // The plan said "Review", so open the record rather than the raw content —
  // the learner has already been through it once.
  const done = target.status === "completed";

  if (target.contentType === "ppt-module") {
    const catIdx = categories.findIndex((c) => String(c._id) === id);
    if (catIdx < 0) return missing;
    return { catIdx, itemIdx: 0, ...(done ? { forceSection: "Review" } : {}) };
  }

  if (target.contentType === "ppt-item") {
    const at = findLesson(categories, id);
    if (!at) return missing;
    return { ...at, ...(done ? { forceSection: "Review" } : {}) };
  }

  if (target.contentType === "video" || target.contentType === "assessment") {
    // Both live inside a lesson, so the lesson id is what we navigate by.
    if (!target.pptItemId) return missing;
    const at = findLesson(categories, target.pptItemId);
    if (!at) return missing;
    if (done) return { ...at, forceSection: "Review" };
    return target.contentType === "video"
      ? { ...at, forceSection: "Video", videoId: id }
      : { ...at, forceSection: "Warmup", assessmentId: id };
  }

  return missing;
};

/**
 * The four ways a lesson can be consumed. Previously a floating pill bar at the
 * bottom of the viewport, which meant the learner had to look away from the
 * content to find out what else the lesson contained. As tiles above the stage
 * they're an inventory: what exists, which one you're in, what's finished.
 */
const MODES = {
  PPT: { label: "Slides", icon: Slides, sub: "Presentation" },
  Video: { label: "Video", icon: Video, sub: "Recorded lesson" },
  PDF: { label: "Document", icon: Document, sub: "Reference PDF" },
  Warmup: { label: "Warm up", icon: Quiz, sub: "Practice questions" },
  // Not content — the learner's own record of it. "Review" used to be a button
  // label that handed back the same material; this is what it should have
  // opened all along.
  Review: { label: "Review", icon: Award, sub: "Your scores & attempts" },
};

export const Home = () => {
  const [categories, setCategories] = useState([]);
  const [selectedPpt, setSelectedPpt] = useState("");
  const [selectedPPTIndex, setPPTIndex] = useState(null);
  const [openCategory, setOpenCategory] = useState(null);
  const [items, setItems] = useState({});
  const [isFullScreen, setFullScreen] = useState(false);
  const [isUpdated, setUpdated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const section = localStorage.getItem("section");

  const { startPPTTracking, stopPPTTracking } = useScreenTime();

  const [selectedSection, setSelectedSection] = useState("PPT");
  const [isFeedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [categoryIndex, setCategoryIndex] = useState(0);
  const isDeepLinking = useRef(false);
  // The exact assessment/video an assignment asked for, so the viewer opens
  // that one rather than whichever the lesson yields first. Cleared as soon as
  // the learner navigates anywhere themselves.
  const [deepLinkIds, setDeepLinkIds] = useState({});
  // Set only when an assigned item could not be opened at all (deleted), so we
  // say so rather than silently showing unrelated content.
  const [targetMissing, setTargetMissing] = useState(false);

  /**
   * Load an assigned item that isn't in the catalog this page fetched.
   *
   * The catalog is scoped by section and department — it answers "what may this
   * learner browse". An assignment is a separate, explicit grant, so it is
   * fetched on its own authority and shown as a catalog of one. Without this,
   * anything assigned outside the learner's browse scope was unreachable.
   */
  const loadAssignedContent = async (target) => {
    const payload = await openAssignedContent(
      target.contentType,
      target.contentId
    );
    // An assessment the learner holds but whose host lesson is out of their
    // browsable scope comes back on its own, with no lesson to sit inside.
    // It still has to open — the assignment IS the access decision — so it
    // gets the standalone page rather than an "unavailable" card.
    if (!payload?.lesson && payload?.assessment) {
      navigate(`/assessment/${target.contentId}/assessment`);
      return;
    }
    if (!payload?.lesson) {
      setTargetMissing(true);
      return;
    }
    const items = payload.items?.length ? payload.items : [payload.lesson];
    const itemIdx = Math.max(
      items.findIndex((i) => String(i._id) === String(payload.lesson._id)),
      0
    );

    isDeepLinking.current = true;
    setTargetMissing(false);
    setCategories([
      {
        _id: payload.module?._id || payload.lesson._id,
        title: payload.module?.title || payload.lesson.title,
        items,
      },
    ]);
    setOpenCategory(0);
    setPPTIndex(itemIdx);
    setSelectedPpt(items[itemIdx]);
    setDeepLinkIds({
      assessmentId: target.contentType === "assessment" ? target.contentId : undefined,
      videoId: payload.videoId,
    });
    // Same promise as the in-catalog path: a completed item opens its record.
    if (target.status === "completed") setSelectedSection("Review");
    else if (target.contentType === "video") setSelectedSection("Video");
    else if (target.contentType === "assessment") setSelectedSection("Warmup");
  };
  // True only while the feedback dialog is standing between the learner and a
  // module they asked to switch to.
  const pendingSwitch = useRef(false);

  // Completion evidence for the lesson on screen. The legacy PPTHistory time-log
  // still records ATTENDANCE ("they opened it"); this records what the learner
  // actually did — foreground seconds and pages reached — and the SERVER decides
  // completion from it. Reading content only; Video and Warmup track their own.
  const { reportPage, status: progressStatus } = useContentProgress({
    contentType: "ppt-item",
    contentId: selectedPpt?._id,
    // Only track when there is genuinely something to read. A lesson with
    // neither a deck nor a PDF renders an empty viewer, and idling in front of
    // nothing must not earn credit.
    enabled:
      ((selectedSection === "PPT" && !!selectedPpt?.ppt) ||
        (selectedSection === "PDF" && !!selectedPpt?.pdf)) &&
      !!selectedPpt?._id,
  });

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("savedLayouts");
    navigate("/");
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/PPT?section=${section}`, {
          headers: getHeaders().headers,
        });
        setCategories(data?.data || []);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401 || status === 403) handleLogout();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isUpdated) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/PPT?section=${section}`, {
          headers: getHeaders().headers,
        });
        setCategories(data?.data || []);
      } catch (e) {
        console.log(e);
      }
    })();
  }, [isUpdated]);

  useEffect(() => {
    // Wait for the section fetch to land — resolving a deep link against a
    // catalog that hasn't arrived yet would "miss" everything — but do NOT
    // wait for it to be non-empty. A learner sent to a section they can't
    // browse gets an empty catalog, and bailing out here left the target
    // sitting unconsumed in localStorage: the assigned item never opened, and
    // the stale target then hijacked wherever they went next.
    if (loading) return;
    setUpdated(false);

    // Honor a deep-link target set by "My Learning" when opening an assigned
    // item, so we land on the EXACT module/lesson instead of the section's
    // first one. Consumed once so normal navigation is unaffected.
    const target = resolveOpenTarget(categories);
    if (target?.unresolved) {
      // Assigned, but outside the section/department catalog this page loaded.
      // The assignment is the access decision, so fetch it on its own authority
      // and show it as a section of one rather than falling back to unrelated
      // content.
      loadAssignedContent(target.target);
      return;
    }
    if (target) {
      isDeepLinking.current = true;
      setTargetMissing(false);
      setOpenCategory(target.catIdx);
      setPPTIndex(target.itemIdx);
      setSelectedPpt(categories[target.catIdx].items[target.itemIdx]);
      setDeepLinkIds({
        assessmentId: target.assessmentId,
        videoId: target.videoId,
      });
      if (target.forceSection) setSelectedSection(target.forceSection);
      return;
    }

    if (openCategory === null) setOpenCategory(0);
    if (selectedPPTIndex === null) setPPTIndex(0);
    if (!selectedPpt && categories[0]?.items?.length) {
      setSelectedPpt(categories[0].items[0]);
    }
  }, [categories, loading]);

  useEffect(() => {
    if (categories.length && openCategory >= 0 && selectedPPTIndex >= 0) {
      setSelectedPpt(categories[openCategory]?.items[selectedPPTIndex]);
    }
  }, [selectedPPTIndex, openCategory, categories]);

  /**
   * The modes this lesson actually offers. Slides and the PDF appear only when
   * the lesson carries them, so a tile never opens an empty stage.
   */
  const availableModes = useMemo(() => {
    const modes = [];
    if (selectedPpt?.ppt) modes.push("PPT");
    modes.push("Video");
    if (selectedPpt?.pdf) modes.push("PDF");
    modes.push("Warmup");
    // Always last: it reports on the modes before it, so it cannot come first.
    modes.push("Review");
    return modes;
  }, [selectedPpt]);

  useEffect(() => {
    if (!selectedPpt) return;
    // A deep link already chose the mode; anything else starts at the first
    // available one so the learner never lands on a blank stage.
    if (isDeepLinking.current) {
      isDeepLinking.current = false;
      return;
    }
    // The learner moved to another lesson under their own steam, so the
    // assignment's exact-item pins no longer apply.
    setDeepLinkIds({});
    setSelectedSection(availableModes[0]);
  }, [selectedPpt]);

  useEffect(() => {
    if (!selectedPpt) return;
    const category = categories[openCategory];
    if (!category) return;
    setItems({ categoryName: category.title, items: category.items });
  }, [selectedPpt]);

  const handleItemClick = (pptItem, index) => {
    setSelectedPpt(pptItem);
    setPPTIndex(index);
  };

  const handleFullScreen = () => {
    const elem = document.documentElement;
    const inFullScreen =
      document.fullscreenElement ||
      document.mozFullScreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement;

    if (!inFullScreen) {
      const request =
        elem.requestFullscreen ||
        elem.mozRequestFullScreen ||
        elem.webkitRequestFullscreen ||
        elem.msRequestFullscreen;
      request?.call(elem);
    } else {
      const exit =
        document.exitFullscreen ||
        document.mozCancelFullScreen ||
        document.webkitExitFullscreen ||
        document.msExitFullscreen;
      exit?.call(document);
    }
  };

  useEffect(() => {
    const onFullScreenChange = () => {
      const inFullScreen =
        document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement;

      if (inFullScreen) {
        startPPTTracking();
        setFullScreen(true);
      } else {
        stopPPTTracking(selectedPpt.title);
        setFullScreen(false);
      }
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape" && isFullScreen && document.exitFullscreen) {
        document.exitFullscreen();
      }
    };

    document.addEventListener("fullscreenchange", onFullScreenChange);
    document.addEventListener("webkitfullscreenchange", onFullScreenChange);
    document.addEventListener("mozfullscreenchange", onFullScreenChange);
    document.addEventListener("MSFullscreenChange", onFullScreenChange);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("fullscreenchange", onFullScreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullScreenChange);
      document.removeEventListener("mozfullscreenchange", onFullScreenChange);
      document.removeEventListener("MSFullscreenChange", onFullScreenChange);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isFullScreen]);

  const selectPrevious = (index) => {
    if (index) {
      setSelectedPpt(categories[openCategory].items[index - 1]);
      setPPTIndex(index - 1);
    }
  };

  const selectNext = (index) => {
    const total = categories[openCategory].items.length;
    const next = index + 1 === total ? 0 : index + 1;
    setSelectedPpt(categories[openCategory].items[next]);
    setPPTIndex(next);
  };

  /**
   * Switching module asks for feedback on the one being left. Only prompts when
   * the learner is genuinely moving away — re-clicking the open module is a
   * no-op, not a reason to interrupt.
   */
  const handleSwitchModule = (index) => {
    if (typeof index === "number" && openCategory !== index) {
      setCategoryIndex(index);
      pendingSwitch.current = true;
      setFeedbackModalOpen(true);
    }
  };

  /**
   * Dismissing the dialog must only move the learner when the dialog was
   * *caused* by a module switch. Opened deliberately from "Give feedback", it
   * closes and leaves them exactly where they were.
   */
  const handleSwitch = () => {
    if (pendingSwitch.current) {
      pendingSwitch.current = false;
      setOpenCategory(categoryIndex === openCategory ? null : categoryIndex);
      setSelectedSection("PPT");
    }
    setFeedbackModalOpen(false);
  };

  if (isFullScreen && selectedSection === "PPT") {
    return (
      <FullScreenPPTViewer
        ppt={selectedPpt}
        handleFullScreen={handleFullScreen}
        isFullScreen={isFullScreen}
        setCategories={setCategories}
        selectedPPTIndex={selectedPPTIndex}
        selectedCategoryIndex={openCategory}
      />
    );
  }

  const isWarmUpMode = selectedSection === "generalWarmup";

  return (
    <AppShell crumb={section || "Section"} active="section" wide>
      {isFeedbackModalOpen && (
        <ModuleFeedback
          open={isFeedbackModalOpen}
          show={setFeedbackModalOpen}
          handleAgree={handleSwitch}
          module={categories[openCategory]}
          content="How was this module? Your rating and comments go to the training team."
        />
      )}

      <div className="ds-workspace">
        <Sidebar
          categories={categories}
          onItemClick={handleItemClick}
          openCategory={openCategory}
          setOpenCategory={setOpenCategory}
          selectedPPT={selectedPpt}
          selectedPPTIndex={selectedPPTIndex}
          selectedSection={selectedSection}
          currentSection={setSelectedSection}
          handleSwitchModule={handleSwitchModule}
        />

        <div>
          {isWarmUpMode ? (
            <Warm />
          ) : loading ? (
            <Card>
              <EmptyState icon={Module} text="Loading this section…" />
            </Card>
          ) : targetMissing ? (
            <Card>
              <EmptyState
                icon={Module}
                text="This assigned item is no longer available here — it may have been moved or unpublished. Please contact your training team."
              />
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Button variant="outline" size="sm" onClick={() => navigate("/my-learning")}>
                  Back to my learning plan
                </Button>
              </div>
            </Card>
          ) : !selectedPpt ? (
            <Card>
              <EmptyState
                icon={Module}
                text={`No modules have been published in ${section || "this section"} yet.`}
              />
            </Card>
          ) : (
            <>
              <div className="ds-stage__head">
                <div className="ds-grow">
                  <h1 className="ds-h1 ds-truncate">{selectedPpt.title}</h1>
                  {items.categoryName && (
                    <p className="ds-sub">{items.categoryName}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFeedbackModalOpen(true)}
                >
                  Give feedback
                </Button>
              </div>

              <div className="ds-modes">
                {availableModes.map((mode) => {
                  const meta = MODES[mode];
                  const ModeIcon = meta.icon;
                  const isActive = selectedSection === mode;
                  const isDone =
                    mode === "PPT" && progressStatus?.completed === true;
                  return (
                    <button
                      key={mode}
                      type="button"
                      className={`ds-mode${isActive ? " ds-mode--active" : ""}`}
                      onClick={() => setSelectedSection(mode)}
                    >
                      {isDone && (
                        <span className="ds-mode__check">
                          <Check size={11} />
                        </span>
                      )}
                      <span className="ds-mode__icon">
                        <ModeIcon size={21} />
                      </span>
                      <span className="ds-mode__label">{meta.label}</span>
                      <span className="ds-mode__sub">{meta.sub}</span>
                    </button>
                  );
                })}
              </div>

              <SwapIn swapKey={`${selectedPpt._id}-${selectedSection}`} className="ds-stage">
                {selectedSection === "PPT" && (
                  <PPTViewer
                    ppt={selectedPpt}
                    items={items}
                    selectNext={selectNext}
                    selectPrevious={selectPrevious}
                    pptIndex={selectedPPTIndex}
                    onItemClick={handleItemClick}
                    handleFullScreen={handleFullScreen}
                    isFullScreen={isFullScreen}
                    setUpdated={setUpdated}
                    setCategories={setCategories}
                    selectedPPTIndex={selectedPPTIndex}
                    selectedCategoryIndex={openCategory}
                    selectedSection={selectedSection}
                    setSelectedSection={setSelectedSection}
                  />
                )}

                {selectedSection === "Video" && (
                  <VideoSection
                    ppt={selectedPpt}
                    items={items}
                    pptIndex={selectedPPTIndex}
                    onItemClick={handleItemClick}
                    selectedPPTIndex={selectedPPTIndex}
                    selectedSection={selectedSection}
                    setSelectedSection={setSelectedSection}
                    initialVideoId={deepLinkIds.videoId}
                  />
                )}

                {selectedSection === "Warmup" && (
                  <AssessmentSection
                    selectedModule={selectedPpt}
                    assessmentId={deepLinkIds.assessmentId}
                  />
                )}

                {selectedSection === "PDF" && (
                  <PDFViewer pdf={selectedPpt.pdf} onPage={reportPage} />
                )}

                {selectedSection === "Review" && (
                  <ReviewSection pptItemId={selectedPpt._id} />
                )}
              </SwapIn>
            </>
          )}
        </div>
      </div>

      {(selectedSection === "PPT" || selectedSection === "PDF") && (
        <CompletionBadge status={progressStatus} />
      )}
    </AppShell>
  );
};

export default Home;
